import { useState, useEffect, useCallback } from 'react'
import { Target, ChevronDown, RefreshCw, Search, Loader2 } from 'lucide-react'
import { rrGetMonitor, rrGetProductSequences } from '@/actions/rr'

const AV_COLORS = ['#2f9e6b','#3b82c4','#b9772e','#8a5cd1','#c2557a']
function avColor(name: string) { let h = 0; for (const c of name) h = (h * 31 + c.charCodeAt(0)) % AV_COLORS.length; return AV_COLORS[h] }
function initials(n: string) { const p = n.trim().split(' '); return ((p[0]?.[0]??'')+(p[1]?.[0]??'')).toUpperCase() }

interface ProductRow {
  product_id: number; product_title: string
  category_name: string; category_colour: string
  current_position: number; total_agents: number
  next_agent_id: number | null; next_agent_name: string
  assignments_today: number; today_total: number; last_assigned_at: string
}

interface SeqAgent { seq_id: number; sequence_order: number; user_id: number; name: string; is_active: boolean; is_next: boolean }

export default function MonitorPage() {
  const [products, setProducts] = useState<ProductRow[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set())
  const [activeProductId, setActiveProductId] = useState<number | null>(null)
  const [productSearch, setProductSearch] = useState('')
  const [agentSearch, setAgentSearch] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    rrGetMonitor().then(res => {
      const rows: ProductRow[] = (res?.data ?? []) as ProductRow[]
      setProducts(rows)
      if (rows.length) {
        setExpandedCats(new Set(rows.map(r => r.category_name)))
        setActiveProductId(rows[0].product_id)
      }
    }).finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const categories = [...new Set(products.map(p => p.category_name))]
  const activeProduct = products.find(p => p.product_id === activeProductId)

  const [activeSeq, setActiveSeq] = useState<SeqAgent[]>([])
  const [loadingSeq, setLoadingSeq] = useState(false)
  useEffect(() => {
    if (!activeProductId) return
    setLoadingSeq(true)
    rrGetProductSequences(activeProductId).then(res => setActiveSeq((res?.data ?? []) as SeqAgent[])).finally(() => setLoadingSeq(false))
  }, [activeProductId])

  const totalToday = activeProduct?.today_total ?? 0
  const lastAssignment = activeProduct?.last_assigned_at ?? '—'

  const filteredSeq = activeSeq
    .filter(a => !agentSearch || a.name.toLowerCase().includes(agentSearch.toLowerCase()))
    .sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1))

  return (
    <div className="h-full flex flex-col p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brandColor flex items-center justify-center flex-shrink-0">
            <Target className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">Sequence Monitor</h1>
            <p className="text-sm text-gray-500 mt-0.5">View and manually adjust the round-robin pointer for any product.</p>
          </div>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 h-9 px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading
        ? <div className="flex justify-center items-center h-48"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
        : <div className="grid grid-cols-[280px_1fr] gap-4 items-start">
          {/* Left — product list grouped by category */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-b border-gray-100">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Products ({products.length})</span>
            </div>
            <div className="px-3 pt-2 pb-1">
              <div className="flex items-center gap-2 h-8 border border-gray-300 rounded-lg px-2.5 text-gray-400">
                <Search className="w-3.5 h-3.5 flex-shrink-0" />
                <input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Search products…" className="flex-1 text-xs text-gray-900 border-0 outline-none bg-transparent" />
              </div>
            </div>
            <div className="p-2 max-h-[60vh] overflow-y-auto">
              {categories.map(cat => {
                const catProducts = products.filter(p => p.category_name === cat && (!productSearch || p.product_title.toLowerCase().includes(productSearch.toLowerCase())))
                if (!catProducts.length) return null
                const expanded = productSearch ? true : expandedCats.has(cat)
                return (
                  <div key={cat}>
                    <button onClick={() => setExpandedCats(p => { const n = new Set(p); n.has(cat) ? n.delete(cat) : n.add(cat); return n })} className="w-full flex items-center gap-2 px-2 py-2 text-left">
                      <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expanded ? '' : '-rotate-90'}`} />
                      <span className="text-xs font-bold text-gray-700">{cat}</span>
                      <span className="ml-auto text-[10px] text-gray-400">{catProducts.length}</span>
                    </button>
                    {expanded && catProducts.map(p => (
                      <button key={p.product_id} onClick={() => setActiveProductId(p.product_id)}
                        className={`w-full flex items-center gap-2 pl-7 pr-2 py-2 rounded-lg text-sm transition-colors ${p.product_id === activeProductId ? 'bg-green-50 text-brandColor font-bold' : 'text-gray-600 hover:bg-gray-50'}`}>
                        <span className="flex-1 truncate text-left">{p.product_title}</span>
                        <span className="text-[10px] text-gray-400">pos {p.current_position ?? '—'}</span>
                      </button>
                    ))}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Right — sequence detail */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            {activeProduct && (
              <>
                {/* Stats strip */}
                <div className="flex gap-0 border-b border-gray-100">
                  {[
                    { value: activeProduct.current_position ?? '—', label: 'current position' },
                    { value: activeProduct.total_agents, label: 'agents in rotation' },
                    { value: totalToday, label: 'assigned today' },
                    { value: lastAssignment, label: 'last assignment' },
                  ].map((stat, i) => (
                    <div key={i} className="flex-1 px-5 py-4 border-r border-gray-100 last:border-0">
                      <div className="text-2xl font-extrabold text-gray-900">{stat.value}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{stat.label}</div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                  <div>
                    <div className="text-base font-extrabold text-gray-900">{activeProduct.product_title}</div>
                    <div className="text-xs text-gray-400">{activeProduct.category_name}</div>
                  </div>
                  <div className="flex items-center gap-2 h-9 border border-gray-300 rounded-lg px-3 text-gray-400 w-52">
                    <Search className="w-4 h-4 flex-shrink-0" />
                    <input value={agentSearch} onChange={e => setAgentSearch(e.target.value)} placeholder="Search agents…" className="flex-1 text-sm text-gray-900 border-0 outline-none bg-transparent" />
                  </div>
                </div>

                {/* Sequence list */}
                <div className="divide-y divide-gray-50 px-3 py-2 max-h-[45vh] overflow-y-auto">
                  {loadingSeq
                    ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-300" /></div>
                    : filteredSeq.map((a) => (
                      <div key={a.seq_id} className={`flex items-center gap-3 px-2 py-3 rounded-xl ${a.is_next ? 'bg-green-50 ring-1 ring-inset ring-green-200' : ''} ${!a.is_active ? 'opacity-40' : ''}`}>
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-extrabold flex-shrink-0 ${a.is_next ? 'bg-brandColor text-white' : 'bg-gray-100 text-gray-500'}`}>{a.sequence_order}</div>
                        <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0" style={{ background: avColor(a.name) }}>{initials(a.name)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                            {a.name}
                            {a.is_next && <span className="text-[9px] font-extrabold text-white bg-brandColor px-1.5 py-0.5 rounded-full uppercase tracking-wider">Next</span>}
                            {!a.is_active && <span className="text-[9px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full uppercase tracking-wider">Inactive</span>}
                          </div>
                        </div>
                      </div>
                    ))
                  }
                  {!loadingSeq && filteredSeq.length === 0 && agentSearch && (
                    <div className="py-8 text-center text-sm text-gray-400">No agents match "{agentSearch}"</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      }

    </div>
  )
}
